// Columnas con parallax al scrollear — el `ParallaxScroll` de Aceternity que pasó Matías,
// portado a este repo (ADR-2026-10-03-redes-plantillas-con-parallax): las columnas pares
// suben y las impares bajan a medida que la sección pasa por la pantalla, así la grilla
// se "abre" mientras se recorre.
//
// Sin `motion` (no está en el proyecto): el progreso se calcula en `scroll` con un
// `requestAnimationFrame` y se escribe como variable CSS (`--parallax`, de 0 a 1) en el
// contenedor; cada columna se mueve con un `transform` que la lee. Ningún render de React
// por cuadro. El scroller es el ancestro que scrollea (se busca solo), no la ventana: en
// escritorio la ventana no scrollea (ADR-171).
//
// Los ítems se reparten de a uno por columna, en orden (0→col 0, 1→col 1, …), así el orden
// de lectura de izquierda a derecha se mantiene. Las columnas siguen a los mismos cortes
// que la grilla de Plantillas: 2, 3 desde `sm`, 4 desde `lg`. Con
// `prefers-reduced-motion`, quietas.

import React, { useEffect, useMemo, useRef, useState } from 'react';

/** Cuánto se desplaza cada columna al recorrer la sección entera (px). */
const AMPLITUD = 90;
/** Desde qué altura de la vista empieza a moverse (0 = borde de arriba, 1 = el de abajo). */
const LINEA_DE_ARRANQUE = 0.2;

function useColumnas(): number {
  const consultar = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 4;
    if (window.matchMedia('(min-width: 1024px)').matches) return 4;
    if (window.matchMedia('(min-width: 640px)').matches) return 3;
    return 2;
  };
  const [n, setN] = useState(consultar);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const lg = window.matchMedia('(min-width: 1024px)');
    const sm = window.matchMedia('(min-width: 640px)');
    const alCambiar = () => setN(consultar());
    lg.addEventListener('change', alCambiar);
    sm.addEventListener('change', alCambiar);
    return () => {
      lg.removeEventListener('change', alCambiar);
      sm.removeEventListener('change', alCambiar);
    };
  }, []);
  return n;
}

/** El primer ancestro que scrollea en vertical. */
function scrollerDe(el: HTMLElement | null): HTMLElement | null {
  let n = el?.parentElement ?? null;
  while (n) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
    n = n.parentElement;
  }
  return null;
}

export const ColumnasParallax: React.FC<{
  items: React.ReactNode[];
  /** Separación entre columnas (las de la grilla de Plantillas). */
  className?: string;
  /**
   * Sin parallax: las mismas columnas, quietas y sin el aire de abajo. Recrear lo pide en
   * el shell de mobile (ADR-2026-10-05-recrear-sin-parallax-en-mobile).
   */
  quieto?: boolean;
}> = ({ items, className = 'gap-3 sm:gap-4', quieto = false }) => {
  const n = useColumnas();
  const ref = useRef<HTMLDivElement | null>(null);

  const columnas = useMemo(() => {
    const cols: React.ReactNode[][] = Array.from({ length: n }, () => []);
    items.forEach((it, i) => cols[i % n].push(it));
    return cols;
  }, [items, n]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (quieto) {
      el.style.setProperty('--parallax', '0');
      return;
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const scroller = scrollerDe(el);
    const blanco: HTMLElement | Window = scroller ?? window;
    let pendiente = 0;
    const medir = () => {
      pendiente = 0;
      const caja = el.getBoundingClientRect();
      const vista = scroller ? scroller.getBoundingClientRect() : { top: 0, height: window.innerHeight };
      // Arranca TARDE: en 0 hasta que el borde de arriba de las columnas sube a la línea
      // del 20 % de la vista (o sea, cuando ya se las está mirando de frente, no mientras
      // asoman por abajo), y llega a 1 cuando el borde de abajo cruza esa misma línea.
      // Así, en el momento en que las imágenes se ven bien, todavía están alineadas.
      const linea = vista.top + vista.height * LINEA_DE_ARRANQUE;
      const recorrido = Math.max(1, caja.height);
      const avance = linea - caja.top;
      const p = Math.min(1, Math.max(0, avance / recorrido));
      el.style.setProperty('--parallax', p.toFixed(4));
    };
    const alScrollear = () => {
      if (!pendiente) pendiente = window.requestAnimationFrame(medir);
    };
    medir();
    blanco.addEventListener('scroll', alScrollear, { passive: true });
    window.addEventListener('resize', alScrollear);
    return () => {
      blanco.removeEventListener('scroll', alScrollear);
      window.removeEventListener('resize', alScrollear);
      if (pendiente) window.cancelAnimationFrame(pendiente);
    };
  }, [n, items.length, quieto]);

  return (
    <div
      ref={ref}
      className={`grid items-start ${className}`}
      // El aire de abajo es lo que bajan las columnas impares: sin esto pisarían lo que
      // sigue a la sección.
      style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, paddingBottom: quieto ? 0 : AMPLITUD, '--parallax': 0 } as React.CSSProperties}
    >
      {columnas.map((col, c) => (
        <div
          key={c}
          // Quietas, sin `will-change` ni transform: no hace falta una capa por columna.
          className={`flex flex-col ${quieto ? '' : 'will-change-transform'}`}
          style={
            quieto
              ? undefined
              : {
                  // Pares suben, impares bajan.
                  transform: `translateY(calc(var(--parallax) * ${c % 2 === 0 ? -AMPLITUD : AMPLITUD}px))`,
                }
          }
        >
          {col}
        </div>
      ))}
    </div>
  );
};

export default ColumnasParallax;
